import React, { useCallback, useEffect, useState } from "react";
import AtlasMap from "./atlas/AtlasMap";
import NodeTable from "./atlas/NodeTable";
import EvidencePanel from "./atlas/EvidencePanel";
import useAtlas from "./atlas/useAtlas";
import FilterBar from "./network/FilterBar";
import LinkCard from "./network/LinkCard";
import { allLinks, applyFilters, countLinks, readFilters, writeFilters } from "./network/state";

// Map first.
//
// This used to open on a stack of cards with a definition list in each one,
// which is an accurate way to present a web of associations and a dull way to
// meet one. The map is the way in: you start on one thing, open what it touches,
// and keep going. The list is still here, still authoritative, still what a
// screen reader and a printer get — one button away rather than in the way.
function ViewToggle({ onChange, view }) {
  return (
    <div className="viewtoggle" role="group" aria-label="How to show these correspondences">
      {[["map", "Map"], ["table", "Table"], ["list", "List"]].map(([key, label]) => (
        <button
          key={key}
          type="button"
          className={`viewtoggle__tab${view === key ? " viewtoggle__tab--on" : ""}`}
          aria-pressed={view === key}
          data-testid={`correspondence-view-${key}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// The view is in the URL, so "open the table for this thing" can be a link
// somebody sends to somebody else.
function initialView() {
  if (typeof window === "undefined") return "map";
  const wanted = new URLSearchParams(window.location.search).get("view");
  return ["map", "table", "list"].includes(wanted) ? wanted : "map";
}

export default function CorrespondenceNetwork({ atlas: atlasPayload, payload }) {
  const [view, setView] = useState(initialView);
  const [filters, setFilters] = useState(() => readFilters());

  const [selected, setSelected] = useState(null);
  const atlas = useAtlas({
    focusKey: selected?.type === "node" ? selected.key : null,
    payload: atlasPayload,
    start: atlasPayload.focus
  });

  const groups = applyFilters(payload.groups, filters);
  const shown = countLinks(groups);
  const total = countLinks(payload.groups);

  useEffect(() => { writeFilters(filters); }, [filters]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (view === "map") params.delete("view"); else params.set("view", view);
    const query = params.toString();
    window.history.replaceState({}, "", query ? `?${query}` : window.location.pathname);
  }, [view]);

  const schools = Object.fromEntries(
    allLinks(payload.groups)
      .filter((link) => link.school)
      .map((link) => [link.school.slug, link.school.name])
  );

  const selectNode = useCallback((node) => {
    atlas.open(node.key);
    setSelected({ type: "node", key: node.key });
  }, [atlas]);

  const selectEdge = useCallback((edge) => setSelected({ type: "edge", id: edge.id }), []);

  // Whether THIS node has anything, not whether the web does: the atlas is the
  // whole graph, so checking it would have said Aries was fine because Saturn
  // is.
  if (total === 0) {
    return (
      <p className="section-index__lede" data-testid="correspondence-empty">
        This study does not yet include any researched correspondences for {payload.node.name}.
        {" "}<a href="/correspondences">Browse the researched correspondences.</a>
      </p>
    );
  }

  return (
    <div className="correspondence-network">
      <div className="correspondence-network__controls">
        <ViewToggle view={view} onChange={setView} />
        {view === "list" ? (
          <FilterBar
            facets={payload.facets}
            filters={filters}
            onChange={setFilters}
            schools={schools}
            shown={shown}
            total={total}
          />
        ) : null}
      </div>

      {view === "map" ? (
        <div className="atlas-layout">
          <AtlasMap
            atlas={atlas}
            onSelectEdge={selectEdge}
            onSelectNode={selectNode}
            selected={selected}
          />
          <EvidencePanel
            atlas={atlas}
            onClose={() => setSelected(null)}
            onSelectEdge={selectEdge}
            onSelectNode={selectNode}
            schools={atlasPayload.schools}
            selected={selected}
          />
        </div>
      ) : null}

      {/* The evidence sits beside the table as well as beside the map: "read
          them" in a row must not send a reader somewhere else to do it. */}
      {view === "table" ? (
        <div className="atlas-layout">
          <NodeTable
            atlas={atlas}
            node={atlas.nodesByKey.get(atlas.origin) || { key: atlas.origin, name: payload.node.name }}
            onSelectEdge={selectEdge}
            schools={atlasPayload.schools}
          />
          <EvidencePanel
            atlas={atlas}
            onClose={() => setSelected(null)}
            onSelectEdge={selectEdge}
            onSelectNode={selectNode}
            schools={atlasPayload.schools}
            selected={selected}
          />
        </div>
      ) : null}

      {view === "list" ? (
        <>
          {shown === 0 ? (
            <p className="correspondence__note" data-testid="correspondence-none-match">
              No correspondence matches these filters. All {total} are currently hidden.
            </p>
          ) : null}

          {groups.map((group) => (
            <section
              key={group.label}
              className="correspondence__group"
              aria-label={group.label}
              data-testid={`correspondence-group-${group.label}`}
            >
              <h2 className="history__heading">{group.label}</h2>
              <ul className="correspondence__cards">
                {group.links.map((link) => <LinkCard key={link.id} link={link} />)}
              </ul>
            </section>
          ))}
        </>
      ) : null}
    </div>
  );
}
